import { FCX, getFormattedThousand, Tooltip, useUuid } from "@datassential/platform-ui-lib";
import './MentionsChart.scss';
import { useCallback, useMemo } from "react";
import {
    getAxisLabelString,
    getAxisValues
} from "@pages/Food/components/SectionSocial/components/MentionsChart/MentionsChart.helpers";
import dayjs from "dayjs";
import { ISocialSlice } from "@models/FoodSocial";
import cn from "classnames";

const FoodMentionsChart: FCX<Pick<ISocialSlice, 'data' | 'sequences'> & {
    selectedMonth: number | null;
    selectedYear: string | null;
    onBarClick?: (month: number | null, year: string | null) => void;
}> = ({
    data: rawData,
    sequences,
    selectedMonth,
    selectedYear,
    onBarClick = () => {},
}) => {
    const tooltipId = useUuid();

    const data = useMemo(
        () => rawData.filter(year => year.months.length > 0),
        [rawData]
    );

    const maxValue = useMemo(
        (): number => {
            return Math.max(
                ...data.flatMap(
                    ({ months }) => {
                        return months.flatMap(
                            ({ values }) => {
                                return values.reduce((acc, i) => acc + i, 0);
                            }
                        )
                    }
                )
            );
        },
        [data]
    );

    const yAxisLabels = useMemo(
        () => getAxisValues(maxValue),
        [maxValue]
    );

    const getBarHeightStyle = useCallback(
        (value: number): string => {
            return `${(value / yAxisLabels[0]) * 100}%`;
        },
        [yAxisLabels]
    );

    const getItemTooltipId = useCallback(
        (month: number, year: string): string => {
            return `${tooltipId}-${year}-${month}`;
        },
        [tooltipId]
    );

    return (
        <div className="FoodMentionsChart">
            <div className="FoodMentionsChart__y-axis FoodSectionSocial__blur">
                {yAxisLabels.map((label, index) => (
                    <div
                        key={index}
                        className="FoodMentionsChart__y-axis-label"
                        data-testid={`textFoodSocialMentionsChartYAxisLabel-${index}`}
                    >
                        {getAxisLabelString(label)}
                    </div>
                ))}
            </div>
            <div className="FoodMentionsChart__years">
                {data.map(({ yearLabel, months }, yearIndex) => (
                    <div
                        key={yearLabel}
                        className={cn(
                            "FoodMentionsChart__year",
                            `size-${months.length}`
                        )}
                        data-testid={`blockFoodSocialMentionsChartYear-${yearIndex}`}
                    >
                        <div className="FoodMentionsChart__year-months">
                            {months.map(({ month, values }, monthIndex) => {
                                const isActive = selectedMonth === month && selectedYear === yearLabel;

                                return (
                                    <div
                                        key={month}
                                        className={cn(
                                            "FoodMentionsChart__month",
                                            isActive && 'is-active',
                                        )}
                                        data-tooltip-id={getItemTooltipId(month, yearLabel)}
                                        data-testid={`blockFoodSocialMentionsChartMonth-${yearIndex}-${monthIndex}`}
                                        onClick={() => onBarClick(
                                            isActive ? null : month,
                                            isActive ? null : yearLabel
                                        )}
                                    >
                                        {values.map((value, index) => (
                                            <div
                                                key={index}
                                                className="FoodMentionsChart__value FoodSectionSocial__blur"
                                                data-testid={`blockFoodSocialMentionsChartMonth-${yearIndex}-${monthIndex}-${index}`}
                                                style={{
                                                    height: getBarHeightStyle(value),
                                                    backgroundColor: sequences[index].color,
                                                }}
                                            />
                                        ))}
                                        <Tooltip
                                            id={getItemTooltipId(month, yearLabel)}
                                            place="right"
                                            variant="dark"
                                            style={{
                                                backgroundColor: '#2D3A4C',
                                                filter: 'drop-shadow(0px 4px 4px rgba(255, 255, 255, 0.9))',
                                                color: '#fff',
                                            }}
                                            delayShow={200}
                                            offset={0}
                                        >
                                            <div className="FoodMentionsChart__tooltip">
                                                <div><b>{dayjs(`${yearLabel}-${month}-01`).format('MMMM YYYY')}</b></div>
                                                <div>
                                                    Total: {getFormattedThousand(values.reduce((acc, i) => acc + i, 0))}
                                                </div>
                                                {values.length > 1 && values.map((value, index) => (
                                                    <div
                                                        key={index}
                                                        className="FoodMentionsChart__tooltip-item"
                                                    >
                                                        <div
                                                            className="FoodMentionsChart__tooltip-color"
                                                            style={{ backgroundColor: sequences[index].color }}
                                                        />
                                                        <div>
                                                            {sequences[index].title}: {getFormattedThousand(value)}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </Tooltip>
                                    </div>
                                );
                            })}
                        </div>
                        <div
                            className="FoodMentionsChart__year-label"
                            data-testid={`textFoodSocialMentionsChartYearLabel-${yearIndex}`}
                        >
                            {yearLabel}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default FoodMentionsChart;